import React, { createContext, useContext, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

const ContactGroupsContext = createContext();
export const useContactGroups = () => useContext(ContactGroupsContext);

export const ContactGroupsProvider = ({ children }) => {
  const { currentUser } = useAuth(); // get token from context
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const request = async (url, method = "GET", data = null) => {
    const token = currentUser?.token;
    if (!token) throw new Error("Unauthorized: No token available");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const options = { method, headers };
    if (data) options.body = JSON.stringify(data);

    const res = await fetch(`https://zola.technology/api${url}`, options);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData?.detail || "API request failed");
    }

    return res.json();
  };

  const fetchGroups = useCallback(async (page = null, pageSize = null) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (page) params.append("page", page);
      if (pageSize) params.append("page_size", pageSize);
      const query = params.toString() ? `?${params}` : "";

      const response = await request(`/company/contact-groups/list/${query}`);
      setGroups(response || []);
      return response;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const createGroup = async (data) => {
    return request("/company/contact-groups/add/", "POST", data);
  };

  const addContactsToGroup = async (groupId, contacts) => {
    return request(`/company/contact-groups/${groupId}/contacts/add/`, "POST", { contacts });
  };

  const removeContactsFromGroup = async (groupId, contacts) => {
    return request(`/company/contact-groups/${groupId}/contacts/remove/`, "POST", { contacts });
  };

  const listGroupContacts = async (groupId, page = null, pageSize = null) => {
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (pageSize) params.append("page_size", pageSize);
    const query = params.toString() ? `?${params}` : "";
    return request(`/company/contact-groups/${groupId}/contacts/list/${query}`);
  };

  const getGroupById = async (groupId) => {
    return request(`/company/contact-groups/${groupId}/get/`);
  };

  return (
    <ContactGroupsContext.Provider
      value={{
        groups,
        loading,
        fetchGroups,
        createGroup,
        addContactsToGroup,
        removeContactsFromGroup,
        listGroupContacts,
        getGroupById,
      }}
    >
      {children}
    </ContactGroupsContext.Provider>
  );
};
